import React from "react"
import TableHeaderColumn from "material-ui/Table/TableHeaderColumn"
import autobind from "class-autobind"
import FontIcon from "material-ui/FontIcon"
import strings from "../../localization/strings"
import AuthenticationStore from "../../stores/AuthenticationStore"
import { green500, green800, grey500, grey800 } from "material-ui/styles/colors"
import * as permissionHelper from "../../helpers/permissionHelper"
import moment from "moment"
import {
	IDateRange, ISuspensePunchData, IPayrollStatusDocument, EPunchSetApprovalType, ELoggedInUserType, ITimecardDay,
	IPunchSetDocument, ITimecardPriorPeriodData
} from "../../models"
import * as colorHelper from "../../helpers/colorHelper"
import ApproveIcon from "../approve/ApproveIcon"
import AccountStore from "../../stores/AccountStore"
import TimecardSuspenseInfoIcon from "./TimecardSuspenseInfoIcon"
import * as payrollStatusHelper from "../../helpers/payrollStatusHelper"

interface IProps {
	userId: string
	timecardPriorPeriodData: ITimecardPriorPeriodData
	fullPunchSets: IPunchSetDocument[]
	hasDailyNote: boolean
	timecardDay: ITimecardDay
	lockData: IPayrollStatusDocument[]
	date: number
	showApproveIconOnBottom: boolean
	loggedInUserType: ELoggedInUserType
	filterByPunchHierarchy: boolean
	suspensePunchData: ISuspensePunchData
	openDailyHistoryDrawer(date: number): void
	handleApproveStatus(status: "unapprove" | "approve", dayOfWeekIndex: number, punchSetApprovalType: EPunchSetApprovalType): void

	priorPeriodAdjustMode: boolean
	allowPriorPeriodAdjust: boolean

	virtualTimecard: boolean
	handleOpenVirtualTimecardPopup(): void

	// if Job->Timecard, it's not full timecard object. so let's hide Info icon to show Notes, Audits and WorkHistory.
	hideInfoIcon?: boolean
}
interface IState {
	infoIconHover: boolean
}

export default class EmployeeTimecardTableHeaderItem extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		this.state = {
			infoIconHover: false
		}
		autobind(this)
	}

	handleApproveStatus(status, punchSetApprovalType) {
		if (punchSetApprovalType === EPunchSetApprovalType.Supervisor &&
			(
				!permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ApproveTimecards) &&
				!permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.ApproveEmployeeExpense)
			)
		) return
		this.props.handleApproveStatus(status, this.props.date, punchSetApprovalType)
	}

	render() {
		const { timecardDay } = this.props
		const startDayTime = timecardDay.LocalStartDayTime.endsWith("Z") ? timecardDay.LocalStartDayTime : timecardDay.LocalStartDayTime + "Z"
		const endDayTime = timecardDay.LocalEndDayTime.endsWith("Z") ? timecardDay.LocalEndDayTime : timecardDay.LocalEndDayTime + "Z"

		const colorByPayPeriod = this.props.priorPeriodAdjustMode ?
			colorHelper.getTimecardPrimaryColor(this.props.timecardPriorPeriodData) :
			this.props.lockData.length <= 1 || moment(startDayTime).utc().isBefore(this.props.lockData[0].EndDate) ?
				"#616161" :
				"#D0D0D0"

		let payPeriodInfo = ""
		let showLockIcon = false
		let locked = false
		if (!this.props.priorPeriodAdjustMode) {
			if (this.props.lockData.length !== 0) {
				if (this.props.date === 0) {
					payPeriodInfo = `${moment(this.props.lockData[0].StartDate).utc().format("MMM D")} - ${moment(this.props.lockData[0].EndDate).utc().format("MMM D")} ${strings.Pay_Period}`
					showLockIcon = payrollStatusHelper.isLocked([this.props.lockData[0]], this.props.userId, this.props.loggedInUserType)
				}
				else if (
					this.props.lockData.length === 2 &&
					moment(startDayTime).utc().isSame(moment(this.props.lockData[1].StartDate), "day")
				) {
					payPeriodInfo = `${moment(this.props.lockData[1].StartDate).utc().format("MMM D")} - ${moment(this.props.lockData[1].EndDate).utc().format("MMM D")} ${strings.Pay_Period}`
					showLockIcon = payrollStatusHelper.isLocked([this.props.lockData[1]], this.props.userId, this.props.loggedInUserType)
				}
				locked = payrollStatusHelper.isLocked([moment(startDayTime).utc().isBefore(moment(this.props.lockData[0].EndDate)) ? this.props.lockData[0] : this.props.lockData[1]], this.props.userId, this.props.loggedInUserType)
			}
		}

		let nonStandardStartTime = false
		let dayInfo = <div style={styles.dayNumberStyle}>{moment(startDayTime).utc().format("MMMM DD")}</div>
		if (moment(startDayTime).utc().hour() > 0) {
			dayInfo = <div style={styles.dayNumberStyle}>{moment(startDayTime).utc().format("M/D ha")}–<br />{moment(endDayTime).utc().format("M/D h:mma")}</div>
			nonStandardStartTime = true
		}

		let headerHeight = 70
		if (!this.props.filterByPunchHierarchy && this.props.showApproveIconOnBottom) {
			headerHeight += 10
		}

		if (nonStandardStartTime) {
			headerHeight += 20
		}

		const dayPunchSets = !this.props.priorPeriodAdjustMode && this.props.timecardDay.PunchSets.map(timecardPS => this.props.fullPunchSets.find(i => i.Id === timecardPS.Id))
		const dayExpenses = !this.props.priorPeriodAdjustMode && (this.props.timecardDay.Expenses || []).concat(this.props.timecardDay.TimeExpenses || [])

		const timecardDayRange: IDateRange = {
			StartDate: timecardDay.LocalStartDayTime,
			EndDate: timecardDay.LocalEndDayTime
		}

		const employeeApproveActionIcon = (
			<ApproveIcon
				key="employee"
				approvalType={EPunchSetApprovalType.Employee}
				toolTipPosition="bottom"
				toolTipArrow="center"
				identify={`employeeapprove${this.props.date}`}
				width={16}
				punchSets={dayPunchSets}
				expenses={dayExpenses}
				handleApproveStatus={!locked ? status => this.handleApproveStatus(status, EPunchSetApprovalType.Employee) : null}
			/>
		)
		const employeeApproveIcon = (
			<ApproveIcon
				key="employee"
				approvalType={EPunchSetApprovalType.Employee}
				toolTipPosition="bottom"
				toolTipArrow="center"
				identify={`employeeapprove${this.props.date}`}
				width={16}
				punchSets={dayPunchSets}
				expenses={dayExpenses}
			/>
		)
		const supervisorApproveActionIcon = (
			<ApproveIcon
				key="supervisor"
				approvalType={EPunchSetApprovalType.Supervisor}
				toolTipPosition="bottom"
				toolTipArrow="center"
				identify={`supervisorapprove${this.props.date}`}
				width={16}
				punchSets={dayPunchSets}
				expenses={dayExpenses}
				handleApproveStatus={!locked ? status => this.handleApproveStatus(status, EPunchSetApprovalType.Supervisor) : null}
			/>
		)
		const supervisorApproveIcon = (
			<ApproveIcon
				key="supervisor"
				approvalType={EPunchSetApprovalType.Supervisor}
				toolTipPosition="bottom"
				toolTipArrow="center"
				identify={`supervisorapprove${this.props.date}`}
				width={16}
				punchSets={dayPunchSets}
				expenses={dayExpenses}
			/>
		)
		const extSupervisorApproveIcon = (
			AccountStore.getExtSupApproveTimecardEnabled() &&
			<ApproveIcon
				key="extsupervisor"
				approvalType={EPunchSetApprovalType.ExtSupervisor}
				toolTipPosition="bottom"
				toolTipArrow="center"
				identify={`extsupervisorapprove${this.props.date}`}
				width={16}
				punchSets={dayPunchSets}
				expenses={[]}
			/>
		)
		const suspenseInfoIcon = (
			!this.props.virtualTimecard &&
			<div style={{ width: 20, marginLeft: 0 }} key="suspense">
				<TimecardSuspenseInfoIcon
					keyStr={this.props.date}
					dateRange={timecardDayRange}
					suspensePunchData={this.props.suspensePunchData}
					handleOpenVirtualTimecardPopup={this.props.handleOpenVirtualTimecardPopup}
				/>
			</div>
		)

		return (
			<TableHeaderColumn style={{ padding: 0, verticalAlign: "bottom", height: "100%" }}>
				<div style={{ position: "absolute", left: 0, width: "100%", top: 2, backgroundColor: "white" }}>
					{!this.props.priorPeriodAdjustMode &&
						<div style={{ height: 20, color: colorByPayPeriod }}>
							{showLockIcon &&
								<FontIcon
									className="fa fa-lock"
									style={{ fontSize: 12, color: colorByPayPeriod, marginRight: 5 }}
								/>
							}
							{payPeriodInfo}
						</div>
					}
					<div style={{ height: 7, backgroundColor: colorByPayPeriod }} />
				</div>
				<div
					style={{
						display: "flex",
						textAlign: "left",
						paddingLeft: 20,
						paddingRight: 20,
						alignItems: this.props.showApproveIconOnBottom ? "flex-start" : "center",
						justifyContent: this.props.showApproveIconOnBottom ? "flex-end" : "flex-start",
						flexDirection: this.props.showApproveIconOnBottom ? "column" : "row",
						height: headerHeight,
						color: this.props.priorPeriodAdjustMode && colorByPayPeriod,
						backgroundColor: locked && "#EFEFEF"
					}}
				>
					<div style={{ display: "flex", marginTop: !this.props.showApproveIconOnBottom && 10 }}>
						<div style={{ marginTop: 12 }}>
							{!this.props.hideInfoIcon && (!this.props.priorPeriodAdjustMode || this.props.allowPriorPeriodAdjust) &&
								<i
									data-intercom-target={"menu-Employees-Timecards-Btn-OpenDailyHistory"}
									className="fa fa-info-circle"
									style={{ color: this.props.hasDailyNote ? this.state.infoIconHover ? green800 : green500 : this.state.infoIconHover ? grey800 : grey500, marginRight: 5, cursor: "pointer", display: "inline-block" }}
									onMouseOver={() => this.setState({ infoIconHover: true })}
									onMouseOut={() => this.setState({ infoIconHover: false })}
									onClick={() => this.props.openDailyHistoryDrawer(this.props.date)}
								/>
							}
						</div>
						<div>
							<div style={{ fontSize: 18 }}>{moment(startDayTime).utc().format("dddd")}</div>
							{dayInfo}
						</div>
					</div>
					{!this.props.priorPeriodAdjustMode && !this.props.filterByPunchHierarchy && this.props.handleApproveStatus &&
						<div style={{ height: 20, width: "-webkit-fill-available" }}>
							{(timecardDay.PunchSets.length + timecardDay.Expenses.length + timecardDay.TimeExpenses.length > 0) &&
								<div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginLeft: !this.props.showApproveIconOnBottom && 5 }}>
									{this.props.loggedInUserType === ELoggedInUserType.Employee ?
										[
											AccountStore.getEmployeeApproveTimecardEnabled() &&
											employeeApproveActionIcon,
											supervisorApproveIcon,
											extSupervisorApproveIcon,
											suspenseInfoIcon
										] :
										[
											supervisorApproveActionIcon,
											AccountStore.getEmployeeApproveTimecardEnabled() &&
											employeeApproveIcon,
											extSupervisorApproveIcon,
											suspenseInfoIcon
										]
									}
								</div>
							}
						</div>
					}
				</div>
			</TableHeaderColumn >
		)
	}
}

const styles = {
	infoIcon: {
		textAlign: "center",
		cursor: "pointer"
	},
	unlockColHeader: {
		display: "flex",
		textAlign: "left",
		paddingLeft: 24,
		paddingRight: 24,
		alignItems: "center"
	},
	lockColHeader: {
		display: "flex",
		textAlign: "left",
		paddingLeft: 24,
		paddingRight: 24,
		alignItems: "center",
		backgroundColor: "#EFEFEF"
	},
	dayNumberStyle: {
		fontSize: 12,
		width: "100%"
	},
}